// src/SearchBar.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./css/SearchBar.css";

const DEFAULT_TAGS = ["roadtrip", "mountains", "beaches", "food", "photography", "bike"];

function normalize(s = "") {
  return s.toLowerCase().trim();
}

/**
 * SearchBar
 * props:
 *  - users: array of user objects (optional)
 *  - trips: array of trip objects (optional)  <- accepted but ignored
 *  - tags: optional array of tags to search (falls back to DEFAULT_TAGS) <- accepted but ignored
 *  - minChars: number (default 3) — minimum chars before autocomplete triggers
 *  - debounceMs: number (default 300)
 */
export default function SearchBar({
  users = [],
  trips = [], // accepted for compatibility but not used
  tags = DEFAULT_TAGS, // accepted for compatibility but not used
  minChars = 3,
  debounceMs = 300,
}) {
  const [query, setQuery] = useState("");
  // only keep users in results
  const [results, setResults] = useState({
    users: [],
    trips: [],
    aiTrips: [],
    guides: [],
    places: [],
  });

  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // flatten results for keyboard navigation — only users
  const flatResults = useMemo(() => {
    const items = [];

    results.guides?.forEach((g) => {
      items.push({
        type: "guide",
        id: g.id,
        label: g.title || g.guide?.title,
        subtitle: g.city?.name || g.country?.name || "",
        payload: g,
      });
    });

    results.places?.forEach((p) => {
      items.push({
        type: "place",
        id: p.id,
        label: p.name,
        subtitle: p.city || "",
        payload: p,
      });
    });

    results.trips?.forEach((t) => {
      items.push({
        type: "trip",
        id: t.id,
        label: t.title,
        subtitle: t.destination || "",
        payload: t,
      });
    });

    results.aiTrips?.forEach((t) => {
      items.push({
        type: "ai_trip",
        id: t.id,
        label: t.title,
        subtitle: "AI Trip",
        payload: t,
      });
    });

    results.users?.forEach((u) => {
      items.push({
        type: "user",
        id: u.id,
        label: u.displayName || u.handle || u.email,
        subtitle: u.handle ? `@${u.handle}` : "",
        avatar: u.photoURL || u.avatar || u.profilePic || "", // <-- add this
        payload: u,
      });
    });

    return items;
  }, [results]);


  // CLIENT-SIDE DEBOUNCE (fast local filtering) — only users
  useEffect(() => {
    if (!users || users.length === 0) return; // 🔥 disable local filter if no users passed

    const q = normalize(query);
    if (!q || q.length < minChars) {
      setResults({ users: [] });
      setShowDropdown(false);
      setActiveIndex(-1);
      return;
    }

    const timer = setTimeout(() => {
      const matchedUsers = users
        .filter((u) =>
          [u.handle, u.displayName, u.location, u.email]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q))
        )
        .slice(0, 8);

      setResults({ users: matchedUsers });
      setShowDropdown(true);
      setActiveIndex(-1);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, users, minChars, debounceMs]);


  // SERVER-SIDE DEBOUNCED FETCH — only ask API for users
  useEffect(() => {
    if (!query || query.length < minChars) {
      return;
    }

    const t = setTimeout(async () => {
      try {
        setLoading(true);   // 🔥 START LOADER

        const base = process.env.REACT_APP_API_BASE || "";
        const url = `${base}/api/search?q=${encodeURIComponent(query)}`;
        const res = await fetch(url, { cache: "no-store" });
        const text = await res.text();

        if (!res.ok) {
          console.warn("Search API error", res.status);
          setLoading(false);
          return;
        }

        if (!text) {
          setResults({ users: [] });
          setShowDropdown(true);
          setLoading(false);
          return;
        }

        const json = JSON.parse(text);

        setResults({
          users: json.users || [],
          trips: json.trips || [],
          aiTrips: json.aiTrips || [],
          guides: json.guides || [],
          places: json.places || [],
        });

        setShowDropdown(true);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);  // 🔥 STOP LOADER
      }
    }, debounceMs);

    return () => clearTimeout(t);
  }, [query, minChars, debounceMs]);

  // keyboard nav
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setShowDropdown(true);
      setActiveIndex((i) => {
        const next = i + 1;
        return Math.min(next, flatResults.length - 1);
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && flatResults[activeIndex]) {
        e.preventDefault();
        handleSelect(flatResults[activeIndex]);
      } else {
        navigate(`/explore?q=${encodeURIComponent(query)}`);
        setShowDropdown(false);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const handleSelect = (item) => {
    setShowDropdown(false);
    setActiveIndex(-1);
    if (!item) return;
    // only user type possible here
    if (item.type === "user") {
      navigate(`/@${item.payload.handle || item.payload.id}`);
    }
    if (item.type === "trip") {
      navigate(
        `/@${item.payload.ownerHandle}/${item.payload.slug || item.payload.id}`
      );
    }
    if (item.type === "ai_trip") {
      navigate(`/ai/${item.payload.slug || item.payload.id}`);
    }
    if (item.type === "guide") {
      navigate(`/${item.payload.slug}`);
    }
    if (item.type === "place") {
      navigate(`/${item.payload.slug}`);
    }

  };

  // close when clicking outside
  useEffect(() => {
    const onDocClick = (ev) => {
      if (!searchRef.current) return;

      const clickedInside = searchRef.current.contains(ev.target);

      if (!clickedInside) {
        setShowDropdown(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleFocus = () => {
    if (query && query.length >= minChars && results.users.length) {
      setShowDropdown(true);
    }
  };

  return (
    <div className="hp-search" ref={searchRef}>
      <div className="search-input-wrap">
        <input
          ref={inputRef}
          className="search-input"
          placeholder="Search guides, trips, users and ai plans..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={onKeyDown}
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls="search-dropdown-listbox"
        />

        <div className="input-right">
          {loading ? (
            <div className="search-loader" />
          ) : (
            query && (
              <button
                type="button"
                className="search-clear"
                onClick={() => {
                  setQuery("");
                  setResults({ users: [] });
                  setShowDropdown(false);
                  inputRef.current?.focus();
                }}
                aria-label="Clear search"
              >
                ✕
              </button>
            )
          )}
        </div>
      </div>

      {/* Dropdown */}
      <div
        id="search-dropdown-listbox"
        role="listbox"
        className={`search-dropdown ${showDropdown ? "show" : ""}`}
        aria-hidden={!showDropdown}

      >
        {flatResults.length === 0 ? (
          <div className="search-empty">No people found — try another search</div>
        ) : (
          flatResults.map((r, idx) => (
            <div
              key={`${r.type}-${r.id}-${idx}`}
              role="option"
              aria-selected={idx === activeIndex}
              className={`search-item ${idx === activeIndex ? "active" : ""}`}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={(ev) => {
                ev.preventDefault();
                handleSelect(r);
              }}
            >
              <div className="item-left">
                {r.avatar ? (
                  <img src={r.avatar} alt="" className="item-avatar" />
                ) : (
                  <div className="item-avatar placeholder">
                    {r.label?.charAt(0)?.toUpperCase()}
                  </div>
                )}
              </div>

              <div className="item-body">
                <div className="item-title">{r.label}</div>
                <div className="item-sub">{r.subtitle}</div>
              </div>

              <div className="item-right">
                <span className={`type-badge ${r.type}`}>
                  {r.type === "user" && "User"}
                  {r.type === "trip" && "Trip"}
                  {r.type === "ai_trip" && "AI Trip"}
                  {r.type === "guide" && "Guide"}
                  {r.type === "place" && "Place"}
                </span>
              </div>

            </div>
          ))
        )}

        {/* footer */}
        <div className="search-footer">
          <button
            className="see-all-btn"
            onMouseDown={(ev) => {
              ev.preventDefault();
              navigate(`/explore?q=${encodeURIComponent(query)}`);
              setShowDropdown(false);
            }}
          >
            See all results for “{query}”
          </button>
        </div>
      </div>
    </div>
  );
}
